import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import JBImage from 'components/essentials/JBImage';
import JBResponsiveImage from 'components/essentials/JBResponsiveImage';
import BannerTextSection from './BannerTextSection';
import { HOMEPAGE_HERO_TEXT_RESERVATION } from './HomepageHeroPlaceholder';

import definition from './definition.yaml';

// Default react-imgix srcset is ~31 widths up to 8192w. This banner's largest
// slot is ~75vw / ~1440 CSS px, so cap candidates to cut cold imgix variants.
const BANNER_SRCSET_WIDTHS = [360, 540, 720, 960, 1080, 1440, 1600, 1920, 2560];

const ImageWithTextBanner2 = ({
  config = {},
  isFullHeight = false,
  isHomepage = false,
  isHomepageHero = false,
}) => {
  const [isBannerImageLoaded, setIsBannerImageLoaded] = useState(false);

  const onImageLoad = useCallback(() => {
    setIsBannerImageLoaded(true);
  }, []);

  const {
    imageProps,
    alignment = 'left',
    textContent,
    colors,
    ctaColors = {},
    toggleInputType,
    cta,
  } = config;

  const colorsBackgroundHex =
    colors?.backgroundHex ?? definition.props.colors.backgroundHex;

  const imageAltTitle =
    imageProps?.altTitle ?? definition.props.imageProps.altTitle;

  const imageClassName =
    'block h-[50vw] object-cover w-full lg:h-full lg:w-full';

  const imageSrcUltraWide = imageProps?.src ?? definition.props.imageProps.src;

  // The Ultra Wide image above is the primary crop - if any of the other sizes
  // are not provided then they fall back to the Ultra Wide version
  const imageSrcExtraWide = imageProps?.srcExtraWide ?? imageSrcUltraWide;
  const imageSrcSquare = imageProps?.srcSquare ?? imageSrcUltraWide;
  const imageSrcWide = imageProps?.srcWide ?? imageSrcUltraWide;

  return (
    <div
      className={cx(
        'flex overflow-hidden flex-col items-stretch lg:h-[560px] [&_h1]:lg:max-w-[700px] 2xl:h-full 2xl:items-center',
        {
          '!bg-white': !isBannerImageLoaded,
          'lg:flex-row-reverse': alignment === 'left',
          'lg:flex-row': alignment === 'right',
          'md:h-full lg:h-full': isFullHeight,
          'lg:!h-[560px] 2xl:!items-stretch': isHomepageHero,
        }
      )}
      style={{
        backgroundColor: colorsBackgroundHex,
      }}
    >
      <div
        className="relative flex-1 lg:h-full lg:w-full"
        data-xc={isHomepageHero ? 'homepage-hero-media' : undefined}
      >
        <JBResponsiveImage>
          {!!imageSrcExtraWide && (
            <JBImage
              alt={imageAltTitle}
              ar="2:1"
              auto="format,compress"
              className={imageClassName}
              crop="center"
              lazy={false}
              media="(max-width: 1023px)"
              onLoad={onImageLoad}
              renderAsSource
              srcSetOptions={{ widths: BANNER_SRCSET_WIDTHS }}
              sizes="100vw"
              src={imageSrcExtraWide}
            />
          )}

          {!!imageSrcSquare && (
            <JBImage
              alt={imageAltTitle}
              ar="1:1"
              auto="format,compress"
              className={imageClassName}
              crop="center"
              lazy={false}
              media="(min-width: 1024px) and (max-width: 1151px)"
              onLoad={onImageLoad}
              renderAsSource
              srcSetOptions={{ widths: BANNER_SRCSET_WIDTHS }}
              sizes="50vw"
              src={imageSrcSquare}
            />
          )}

          {!!imageSrcWide && (
            <JBImage
              alt={imageAltTitle}
              ar="3:2"
              auto="format,compress"
              className={imageClassName}
              lazy={false}
              media="(min-width: 1152px) and (max-width: 1440px)"
              onLoad={onImageLoad}
              renderAsSource
              srcSetOptions={{ widths: BANNER_SRCSET_WIDTHS }}
              sizes="60vw"
              src={imageSrcWide}
            />
          )}

          {!!imageSrcExtraWide && (
            <JBImage
              alt={imageAltTitle}
              ar="2:1"
              auto="format,compress"
              className={imageClassName}
              lazy={false}
              media="(min-width: 1441px) and (max-width: 1799px)"
              onLoad={onImageLoad}
              renderAsSource
              srcSetOptions={{ widths: BANNER_SRCSET_WIDTHS }}
              sizes="68vw"
              src={imageSrcExtraWide}
            />
          )}

          {!!imageSrcUltraWide && (
            <JBImage
              alt={imageAltTitle}
              auto="format,compress"
              className={imageClassName}
              lazy={false}
              media="(min-width: 1800px)"
              onLoad={onImageLoad}
              renderAsSource
              srcSetOptions={{ widths: BANNER_SRCSET_WIDTHS }}
              sizes="75vw"
              src={imageSrcUltraWide}
            />
          )}

          {/*
            Picture element fallback `<img>`. Must be a real <img> (not a
            <source>) — the browser uses this as the actual rendered element
            and falls back to it when no <source media> matches. `priority`
            here is what emits fetchpriority="high" for the LCP candidate.

            `width={1}` looks redundant since CSS `w-full` overrides it
            visually, but it appears to coerce react-imgix's auto-srcset
            into picking smaller candidate URLs for this fallback img —
            removing it during CON-8123 showed a measurable desktop LCP
            regression. Restore once CON-8154 puts the <picture> back
            into the SSR HTML so we can measure cleanly and find a less
            hacky solution.
          */}
          {!!imageSrcWide && (
            <JBImage
              alt={imageAltTitle}
              auto="format,compress"
              className={imageClassName}
              htmlAttributes={
                isHomepageHero
                  ? { 'data-xc': 'homepage-hero-image' }
                  : undefined
              }
              lazy={false}
              onLoad={onImageLoad}
              priority
              srcSetOptions={{ widths: BANNER_SRCSET_WIDTHS }}
              sizes="60vw"
              src={imageSrcWide}
              width={1}
            />
          )}
        </JBResponsiveImage>
      </div>

      <BannerTextSection
        config={{ textContent, colors, cta, ctaColors, toggleInputType }}
        isHomepage={isHomepage}
        isHomepageHero={isHomepageHero}
        className={cx('2xl:!h-[revert] 2xl:!self-stretch', {
          [HOMEPAGE_HERO_TEXT_RESERVATION]: isHomepageHero,
        })}
      />
    </div>
  );
};

ImageWithTextBanner2.propTypes = {
  config: PropTypes.shape({
    colors: PropTypes.object,
    cta: PropTypes.object,
    ctaColors: PropTypes.object,
    textContent: PropTypes.object,
    toggleInputType: PropTypes.object,
  }),
  isFullHeight: PropTypes.bool,
  isHomepage: PropTypes.bool,
};

export default React.memo(ImageWithTextBanner2);
